import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UserProfile } from '@pay888/shared';
import { isUserProfile } from '@pay888/shared';
import {
  createUserWithEmailAndPassword,
  getIdTokenResult,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  reload,
  sendEmailVerification as firebaseSendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { clearPendingReferral, readPendingReferral } from '../referrals/referralCapture.js';
import { signInWithApplePopup } from './appleAuth.js';
import {
  getFirebaseAuth,
  getFirebaseFunctions,
  isFirebaseConfigured,
} from '../../services/firebase.js';
import {
  AuthContext,
  type AuthContextValue,
  type AuthDependencies,
  type AuthUser,
} from './authContext.js';

function createDefaultDependencies(): AuthDependencies {
  const auth = getFirebaseAuth();
  return {
    observe: (listener) => onAuthStateChanged(auth, listener),
    signIn: async (email, password) =>
      (await signInWithEmailAndPassword(auth, email, password)).user,
    signUp: async (email, password) =>
      (await createUserWithEmailAndPassword(auth, email, password)).user,
    signInWithGooglePopup: async () => {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      return (await signInWithPopup(auth, provider)).user;
    },
    updateDisplayName: async (user, displayName) => updateProfile(user, { displayName }),
    signInWithApplePopup: () => signInWithApplePopup(auth),
    sendEmailVerification: async (user) => firebaseSendEmailVerification(user),
    reloadUser: async (user) => reload(user),
    signOut: async () => firebaseSignOut(auth),
    sendPasswordReset: async (email) => sendPasswordResetEmail(auth, email),
    ensureProfile: async () => {
      try {
        const callable = httpsCallable<{ referralCode?: string }, unknown>(
          getFirebaseFunctions(),
          'ensureUserProfile'
        );
        const referralCode = readPendingReferral();
        const res = await callable(referralCode ? { referralCode } : {});
        clearPendingReferral();
        return res.data;
      } catch {
        return null;
      }
    },
    hasAdminClaim: async (user) => {
      try {
        return (await getIdTokenResult(user)).claims.admin === true;
      } catch {
        return false;
      }
    },
  };
}

interface AuthProviderProps {
  readonly children: React.ReactNode;
  readonly dependencies?: AuthDependencies;
  readonly firebaseConfigured?: boolean;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  dependencies,
  firebaseConfigured = isFirebaseConfigured(),
}) => {
  const resolvedDependencies = useMemo(
    () => dependencies ?? (firebaseConfigured ? createDefaultDependencies() : null),
    [dependencies, firebaseConfigured]
  );
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(firebaseConfigured || Boolean(dependencies));
  const hydrationSequence = useRef(0);
  const rawUserRef = useRef<User | null>(null);

  const hydrateUser = useCallback(
    async (user: User | null): Promise<void> => {
      rawUserRef.current = user;
      const sequence = ++hydrationSequence.current;
      setCurrentUser(
        user
          ? {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              emailVerified: user.emailVerified,
            }
          : null
      );
      if (!user || !resolvedDependencies) {
        setUserProfile(null);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const [profile, admin] = await Promise.all([
          resolvedDependencies.ensureProfile().catch(() => null),
          resolvedDependencies.hasAdminClaim(user).catch(() => false),
        ]);
        if (sequence !== hydrationSequence.current) return;
        setUserProfile(isUserProfile(profile) ? profile : null);
        setIsAdmin(admin);
      } finally {
        if (sequence === hydrationSequence.current) setIsLoading(false);
      }
    },
    [resolvedDependencies]
  );

  useEffect(() => {
    if (!resolvedDependencies) return;

    if (firebaseConfigured) {
      try {
        const auth = getFirebaseAuth();
        getRedirectResult(auth)
          .then((res) => {
            if (res?.user) void hydrateUser(res.user);
          })
          .catch(() => undefined);
      } catch {
        // Ignored
      }
    }

    return resolvedDependencies.observe((user) => {
      void hydrateUser(user).catch(() => {
        setUserProfile(null);
        setIsAdmin(false);
        setIsLoading(false);
      });
    });
  }, [firebaseConfigured, hydrateUser, resolvedDependencies]);

  const requireDependencies = useCallback((): AuthDependencies => {
    if (!resolvedDependencies) throw new Error('Firebase Authentication is not configured.');
    return resolvedDependencies;
  }, [resolvedDependencies]);

  const signInWithEmail = useCallback(
    async (email: string, password: string): Promise<void> => {
      const user = await requireDependencies().signIn(email.trim(), password);
      await hydrateUser(user);
    },
    [hydrateUser, requireDependencies]
  );

  const signUpWithEmail = useCallback(
    async (displayName: string, email: string, password: string): Promise<void> => {
      const deps = requireDependencies();
      const user = await deps.signUp(email.trim(), password);
      try {
        await deps.updateDisplayName(user, displayName.trim());
      } catch {
        // Non-blocking if displayName update fails
      }
      try {
        await deps.sendEmailVerification(user);
      } catch {
        // Non-blocking if initial trigger fails; user can resend from /verify-email
      }
      await hydrateUser(user);
    },
    [hydrateUser, requireDependencies]
  );

  const signInWithGoogle = useCallback(async (): Promise<void> => {
    const user = await requireDependencies().signInWithGooglePopup();
    await hydrateUser(user);
  }, [hydrateUser, requireDependencies]);

  const signInWithApple = useCallback(async (): Promise<void> => {
    const user = await requireDependencies().signInWithApplePopup();
    await hydrateUser(user);
  }, [hydrateUser, requireDependencies]);

  const updateUserDisplayName = useCallback(
    async (displayName: string): Promise<void> => {
      const rawUser = rawUserRef.current;
      if (!rawUser) throw new Error('No authenticated user available.');
      const deps = requireDependencies();
      await deps.updateDisplayName(rawUser, displayName.trim());
      await deps.reloadUser(rawUser);
      await hydrateUser(rawUser);
    },
    [hydrateUser, requireDependencies]
  );

  const sendVerificationEmail = useCallback(async (): Promise<void> => {
    const rawUser = rawUserRef.current;
    if (!rawUser) throw new Error('No authenticated user available.');
    await requireDependencies().sendEmailVerification(rawUser);
  }, [requireDependencies]);

  const refreshUserVerificationStatus = useCallback(async (): Promise<void> => {
    const rawUser = rawUserRef.current;
    if (!rawUser) throw new Error('No authenticated user available.');
    await requireDependencies().reloadUser(rawUser);
    await hydrateUser(rawUser);
  }, [hydrateUser, requireDependencies]);

  const signOut = useCallback(async (): Promise<void> => {
    const deps = requireDependencies();
    await deps.signOut();
    await hydrateUser(null);
  }, [hydrateUser, requireDependencies]);

  const sendPasswordReset = useCallback(
    async (email: string): Promise<void> => {
      await requireDependencies().sendPasswordReset(email.trim());
    },
    [requireDependencies]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser,
      userProfile,
      isAdmin,
      isLoading,
      isConfigured: firebaseConfigured || Boolean(dependencies),
      signInWithEmail,
      signUpWithEmail,
      signInWithGoogle,
      signInWithApple,
      updateUserDisplayName,
      sendVerificationEmail,
      refreshUserVerificationStatus,
      signOut,
      sendPasswordReset,
    }),
    [
      currentUser,
      dependencies,
      firebaseConfigured,
      isAdmin,
      isLoading,
      refreshUserVerificationStatus,
      sendPasswordReset,
      sendVerificationEmail,
      signInWithEmail,
      signInWithGoogle,
      signInWithApple,
      signOut,
      signUpWithEmail,
      updateUserDisplayName,
      userProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
